import { createContext, useEffect, useRef, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import API from '@utils/axios';
import { getRoleName, ROLE_ADMIN, ROLE_SUPERADMIN } from '@utils/helpers';

const TOKEN_KEY = 'token';

const loadToken = () => {
    return localStorage.getItem(TOKEN_KEY);
};

const saveToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
};

const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
};

const AuthContext = createContext({
    refreshAuth: () => { },
    doLogout: () => { },
    user: null,
});

const AuthProvider = ({ children }) => {

    const { t, i18n } = useTranslation();
    const [user, setUser] = useState(null);
    const hasTokenRefreshed = useRef(false);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);

    /**
     * True si l'utilisateur est connecté
     */
    const isAuthenticated = useCallback(() => {
        return user !== null;
    }, [user]);

    /**
     * True si l'utilisateur est administrateur ou super-administrateur
     */
    const isAdmin = useCallback(() => {
        return user && ( user.role === ROLE_ADMIN || user.role === ROLE_SUPERADMIN );
    }, [user]);

    /**
     * True si l'utilisateur est super-administrateur
     */
    const isSuperAdmin = useCallback(() => {
        return user && user.role === ROLE_SUPERADMIN;
    }, [user]);

    /**
     * True si l'utilisateur doit changer son mot de passe
     */
    const mustChangePassword = useCallback(() => {
        return user && user.force_password_change;
    }, [user]);

    /**
     * Récupérer l'ID du client de l'utilisateur connecté
     */
    const getCurrentCustomerId = useCallback(() => {
        return user ? user.customer_id : null;
    }, [user]);

    const handleLogout = useCallback(() => {
        clearToken();
        navigate('/login', { replace: true });
    }, [navigate]);

    const doLogout = useCallback(async () => {
        try {
            await API.post('/auth/logout');
        } catch (error) {
            // Erreur lors de la déconnexion : ce n'est pas grave
        }
        handleLogout();
    }, [handleLogout]);

    const decodeToken = useCallback((token) => {
        if (!token) return null;
        try {
            const decoded = jwtDecode(token);
            
            // Ajout de propriétés au user
            decoded.user_full_name = decoded.user_first_name + ' ' + decoded.user_last_name;
            decoded.user_initials = (decoded.user_first_name[0] + decoded.user_last_name[0]).toUpperCase();
            decoded.getNameAndRole = () => {
                const roleName = getRoleName(t, decoded.role);
                return roleName === t('User') ? decoded.customer_name :
                    (decoded.customer_name ? decoded.customer_name + ' - ' + roleName : roleName);
            };
            setUser(decoded);
            setLoaded(true);
            return decoded;
        } catch (error) {
            console.error('Failed to decode token:', error);
            setLoaded(true);
            return null;
        }
    }, [setUser, setLoaded, t]);

    const isTokenExpired = useCallback(() => {
        const tokenRaw = loadToken();
        if (!tokenRaw) return true;
        const decoded = decodeToken(tokenRaw);
        if (!decoded) return true;
        const currentTime = Date.now() / 1000;
        return decoded.exp < currentTime;
    }, [decodeToken]);

    const checkSession = useCallback(() => {
        if (isTokenExpired()) {
            handleLogout();
        }
    }, [isTokenExpired, handleLogout]);

    const refreshAuth = useCallback(async (invalidate) => {
        if (!hasTokenRefreshed.current) {
            hasTokenRefreshed.current = true;
            try {
                let url = '/auth/refresh';
                if (invalidate) {
                    url += '?invalidate=true';
                }
                const response = await API.post(url);
                saveToken(response.data.access_token);
                const decoded = decodeToken(response.data.access_token);
                if (decoded.lang !== i18n.language) {
                    await i18n.changeLanguage(decoded.lang);
                }
                hasTokenRefreshed.current = false;
            } catch (error) {
                handleLogout();
            }
        }
    }, [handleLogout, decodeToken, i18n]);

    useEffect(() => {
        checkSession(); // Vérifier si la session est valide + alimenter le state user
        refreshAuth();
    }, [checkSession, refreshAuth]);

    return (
        <AuthContext.Provider value={{ refreshAuth, doLogout, loaded, user, isAdmin, isSuperAdmin, isAuthenticated, getCurrentCustomerId, mustChangePassword }}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => {
    return useContext(AuthContext);
};

export { loadToken, saveToken, clearToken, AuthProvider, AuthContext, useAuth };
